/**
 * Flying Cart Animation
 * Circular product thumbnail flies to the cart on add, and back on decrease.
 * Motion matched to groentebroer.nl: transform + ease-out, fixed duration.
 */
(function () {
  'use strict';

  var FLYER_SIZE = 100;
  var END_SIZE = 30;
  var DURATION_MS = 1500;
  var EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var CART_OFFSET = { x: 0, y: 0 };
  var LOCK_MS = 200;

  class FlyingCartAnimation {
    constructor() {
      this.locks = new WeakMap();
      this.init();
    }

    init() {
      document.addEventListener('submit', (e) => {
        var form = e.target;
        if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
        var button = form.querySelector('[data-product-atc-button]');
        if (!button && form.classList.contains('action-button-form')) {
          button = form.querySelector('button[type="submit"], button');
        }
        if (!button) return;
        setTimeout(() => this.animateToCart(button), 100);
      });

      document.addEventListener('product:added-to-cart', (e) => {
        if (e.detail && e.detail.button) this.animateToCart(e.detail.button);
      });

      document.addEventListener('product:removed-from-cart', (e) => {
        if (e.detail && e.detail.button) this.animateFromCart(e.detail.button);
      });
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    isLocked(button) {
      if (!button) return false;
      return this.locks.has(button) && Date.now() - this.locks.get(button) < LOCK_MS;
    }

    lock(button) {
      if (button) this.locks.set(button, Date.now());
    }

    isMobileStickyBarActive() {
      if (window.matchMedia && window.matchMedia('(min-width: 768px)').matches) return false;
      if (!document.body.classList.contains('mobile-sticky-bar-enabled')) return false;
      if (document.body.classList.contains('mobile-sticky-bar-down')) return false;
      return true;
    }

    firstVisible(selectors) {
      for (var i = 0; i < selectors.length; i++) {
        var nodes = document.querySelectorAll(selectors[i]);
        for (var j = 0; j < nodes.length; j++) {
          var rect = nodes[j].getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight) {
            return nodes[j];
          }
        }
      }
      return null;
    }

    getCartIcon() {
      if (this.isMobileStickyBarActive()) {
        var stickyCart = this.firstVisible([
          '.mobile-sticky-bar .cart-icon-wrap',
          '.mobile-sticky-bar .cart-drawer-button',
          '.mobile-sticky-bar__cart',
        ]);
        if (stickyCart) return stickyCart;
      }

      return this.firstVisible([
        '.header .cart-drawer-button',
        '.header .cart-icon',
        '.cart-drawer-button',
        '.header a[href$="/cart"]',
        '.header [href*="/cart"]',
        '.header__icon--cart',
        '[data-cart-icon]',
        '.cart-icon',
        '[aria-controls="CartDrawer"]',
        'a[href*="/cart"]',
      ]);
    }

    getProductCard(button) {
      if (!button) return null;
      // Do not include the add-to-cart form here: closest() would match the form
      // first, and the product image lives outside that form.
      // Upsell cards can sit inside a product card, so resolve those first.
      return (
        button.closest('.atc-related-upsell__card') ||
        button.closest('.product-card') ||
        button.closest('[data-product-id]')
      );
    }

    queryInCard(card, selector) {
      if (!card) return null;
      var nodes = card.querySelectorAll(selector);
      var skipNestedUpsell = !card.classList.contains('atc-related-upsell__card');
      for (var i = 0; i < nodes.length; i++) {
        if (skipNestedUpsell && nodes[i].closest('[data-atc-related-upsell]')) continue;
        return nodes[i];
      }
      return null;
    }

    getNearbyImage(button) {
      if (!button || !button.closest('.action-button')) return null;

      var card = button.closest('.multicolumn-card');
      if (card) {
        var cardImage = card.querySelector('.multicolumn-card__image img');
        if (cardImage) return cardImage;
      }

      var imageWithText = button.closest('.image-with-text');
      if (imageWithText) {
        var heroImage = imageWithText.querySelector('.image-with-text__media img');
        if (heroImage) return heroImage;
      }

      return null;
    }

    getProductImage(button) {
      var nearbyImage = this.getNearbyImage(button);
      if (nearbyImage) return nearbyImage;

      var productCard = this.getProductCard(button);
      if (!productCard) return null;
      return (
        this.queryInCard(productCard, '[data-product-image] img') ||
        this.queryInCard(productCard, '.atc-related-upsell__img') ||
        this.queryInCard(productCard, '.product-card__image--main img') ||
        this.queryInCard(productCard, '.product-card__image img')
      );
    }

    getProductImageSrc(button) {
      var productImage = this.getProductImage(button);
      if (productImage) return productImage.currentSrc || productImage.src || null;

      var actionButton = button && button.closest('.action-button');
      if (actionButton && actionButton.dataset.flyingImage) return actionButton.dataset.flyingImage;
      return null;
    }

    getProductImageBox(button) {
      var productCard = this.getProductCard(button);
      if (!productCard) return this.getProductImage(button);

      return (
        this.queryInCard(productCard, '[data-product-image]') ||
        this.queryInCard(productCard, '.atc-related-upsell__media') ||
        this.queryInCard(productCard, '.product-card__image--main') ||
        this.queryInCard(productCard, '.product-card__image-wrapper') ||
        this.getProductImage(button)
      );
    }

    getStartRect(button) {
      var startEl = this.getProductImageBox(button) || button;
      if (!startEl) return null;

      var rect = startEl.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) {
        var fallback = this.getProductImage(button) || button;
        if (fallback && fallback !== startEl) {
          rect = fallback.getBoundingClientRect();
        }
      }

      if (rect.width < 2 || rect.height < 2) return null;
      return rect;
    }

    createFlyer(src) {
      var flyingImage = document.createElement('img');
      flyingImage.src = src;
      flyingImage.alt = '';
      flyingImage.classList.add('flying-cart-image');
      flyingImage.setAttribute('aria-hidden', 'true');
      flyingImage.style.position = 'fixed';
      flyingImage.style.zIndex = '99999';
      flyingImage.style.pointerEvents = 'none';
      flyingImage.style.width = FLYER_SIZE + 'px';
      flyingImage.style.height = FLYER_SIZE + 'px';
      flyingImage.style.borderRadius = '9999px';
      flyingImage.style.objectFit = 'cover';
      flyingImage.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.25)';
      flyingImage.style.margin = '0';
      flyingImage.style.maxWidth = 'none';
      flyingImage.style.maxHeight = 'none';
      flyingImage.style.opacity = '1';
      flyingImage.style.transformOrigin = 'center center';
      flyingImage.style.willChange = 'transform, opacity';
      return flyingImage;
    }

    getTravel(startRect, cartRect) {
      var startX = startRect.left + startRect.width / 2;
      var startY = startRect.top + startRect.height / 2;
      var endX = cartRect.left + cartRect.width / 2 + CART_OFFSET.x;
      var endY = cartRect.top + cartRect.height / 2 + CART_OFFSET.y;
      return {
        startX: startX,
        startY: startY,
        dx: endX - startX,
        dy: endY - startY,
        endScale: Math.max(0.1, END_SIZE / FLYER_SIZE),
      };
    }

    placeFlyer(flyingImage, startX, startY) {
      flyingImage.style.left = startX - FLYER_SIZE / 2 + 'px';
      flyingImage.style.top = startY - FLYER_SIZE / 2 + 'px';
      document.body.appendChild(flyingImage);
    }

    pulseCart(cartIcon) {
      if (!cartIcon || typeof cartIcon.animate !== 'function') return;
      cartIcon.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }],
        { duration: 220, easing: EASING }
      );
    }

    animateToCart(button) {
      if (this.prefersReducedMotion()) return;
      if (this.isLocked(button)) return;

      var src = this.getProductImageSrc(button);
      var cartIcon = this.getCartIcon();
      var startRect = this.getStartRect(button);
      if (!src || !cartIcon || !startRect) return;

      var cartRect = cartIcon.getBoundingClientRect();
      if (cartRect.width < 2 || cartRect.height < 2) return;

      this.lock(button);

      var travel = this.getTravel(startRect, cartRect);
      var flyingImage = this.createFlyer(src);
      this.placeFlyer(flyingImage, travel.startX, travel.startY);

      var animation = flyingImage.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
          {
            transform: 'translate(' + travel.dx + 'px, ' + travel.dy + 'px) scale(' + travel.endScale + ')',
            opacity: 0.9,
          },
        ],
        {
          duration: DURATION_MS,
          easing: EASING,
          fill: 'forwards',
        }
      );

      var self = this;
      animation.onfinish = function () {
        flyingImage.remove();
        self.pulseCart(cartIcon);
      };
    }

    animateFromCart(button) {
      if (this.prefersReducedMotion()) return;
      if (this.isLocked(button)) return;

      var src = this.getProductImageSrc(button);
      var cartIcon = this.getCartIcon();
      var startRect = this.getStartRect(button);
      if (!src || !cartIcon || !startRect) return;

      var cartRect = cartIcon.getBoundingClientRect();
      if (cartRect.width < 2 || cartRect.height < 2) return;

      this.lock(button);

      var travel = this.getTravel(startRect, cartRect);
      var flyingImage = this.createFlyer(src);
      this.placeFlyer(flyingImage, travel.startX, travel.startY);

      var animation = flyingImage.animate(
        [
          {
            transform: 'translate(' + travel.dx + 'px, ' + travel.dy + 'px) scale(' + travel.endScale + ')',
            opacity: 0.9,
            offset: 0,
          },
          {
            transform: 'translate(0, 0) scale(1)',
            opacity: 1,
            offset: 0.82,
          },
          {
            transform: 'translate(0, 0) scale(1)',
            opacity: 0,
            offset: 1,
          },
        ],
        {
          duration: DURATION_MS,
          easing: EASING,
          fill: 'forwards',
        }
      );

      animation.onfinish = function () {
        flyingImage.remove();
      };
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      new FlyingCartAnimation();
    });
  } else {
    new FlyingCartAnimation();
  }
})();
